// src/index.ts
export interface FloorplanConfig {
  url?: string;
  key: string;
  access_token: string;
  location_id: string;
  layer_id?: string;
  spaces?: string[];
  room_id?: string; // Optional room ID
  zone_id?: string; // Optional zone ID
  class_names?: string;
  title?: string;
  dark_mode?: boolean;
}

export interface SpaceClickedEvent {
  event: "SPACE_CLICKED";
  space_id: string;
}

export type EventCallback = (event: SpaceClickedEvent) => void;

export class FloorplanSDK {
  private iframe: HTMLIFrameElement | null = null;
  private config: FloorplanConfig;
  private eventListeners: Map<string, EventCallback[]> = new Map();

  constructor(config: FloorplanConfig) {
    this.config = {
      url: config.url ?? "https://floorplan.gospace.app",
      key: config.key,
      access_token: config.access_token,
      location_id: config.location_id,
      layer_id: config.layer_id,
      spaces: config.spaces,
      room_id: config.room_id,
      zone_id: config.zone_id,
      class_names: config.class_names ?? "w-full h-full",
      title: config.title ?? "gospace floorplan",
      dark_mode: config.dark_mode ?? true,
    };

    if (
      !this.config.key ||
      !this.config.access_token ||
      !this.config.location_id
    ) {
      throw new Error(
        "Missing required configuration: key, access_token, and location_id are required"
      );
    }

    if (
      this.config.spaces &&
      (!Array.isArray(this.config.spaces) ||
        this.config.spaces.some(
          (id) => typeof id !== "string" || !/^[a-zA-Z0-9]+$/.test(id)
        ))
    ) {
      throw new Error(
        "Invalid spaces: must be an array of alphanumeric strings"
      );
    }

    if (
      this.config.room_id &&
      (typeof this.config.room_id !== "string" ||
        !/^[a-zA-Z0-9]+$/.test(this.config.room_id))
    ) {
      throw new Error("Invalid room_id: must be an alphanumeric string");
    }

    if (
      this.config.zone_id &&
      (typeof this.config.zone_id !== "string" ||
        !/^[a-zA-Z0-9]+$/.test(this.config.zone_id))
    ) {
      throw new Error("Invalid zone_id: must be an alphanumeric string");
    }
  }

  public init(): void {
    const container = document.getElementById(this.config.key);
    if (!container) {
      throw new Error(`Container with ID ${this.config.key} not found`);
    }

    this.iframe = document.createElement("iframe");
    this.iframe.src = this.config.url;
    this.iframe.class_names = this.config.class_names;
    this.iframe.title = this.config.title;
    container.appendChild(this.iframe);

    this.iframe.addEventListener("load", () => {
      if (!this.iframe || !this.iframe.contentWindow) return;
      this.sendCredentials();
    });

    window.addEventListener("message", this.handleMessage.bind(this));
  }

  private sendCredentials() {
    this.iframe?.contentWindow?.postMessage(
      {
        access_token: this.config.access_token,
        location_id: this.config.location_id,
        layer_id: this.config.layer_id,
        spaces: this.config.spaces,
        room_id: this.config.room_id,
        zone_id: this.config.zone_id,
        dark_mode: this.config.dark_mode,
      },
      new URL(this.config?.url!).origin
    );
  }

  public on(eventType: "SPACE_CLICKED", callback: EventCallback): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType)!.push(callback);
  }

  public off(eventType: "SPACE_CLICKED", callback: EventCallback): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      this.eventListeners.set(
        eventType,
        listeners.filter((cb) => cb !== callback)
      );
    }
  }

  public destroy(): void {
    window.removeEventListener("message", this.handleMessage.bind(this));
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
      this.iframe = null;
    }
    this.eventListeners.clear();
  }

  private handleMessage(event: MessageEvent): void {
    if (event.origin !== new URL(this.config.url!).origin) return;

    if (typeof event.data === "string" && event.data === "ready") {
      this.sendCredentials();
    }

    const { event: eventType, metadata, id } = event.data;

    if (
      eventType === "click" &&
      metadata?.type === "SPACE" &&
      typeof id === "string" &&
      /^[a-zA-Z0-9]+$/.test(id)
    ) {
      const listeners = this.eventListeners.get("SPACE_CLICKED") || [];

      listeners.forEach((callback) =>
        callback({ event: "SPACE_CLICKED", space_id: id })
      );
    }
  }
}
